import { Link } from 'react-router-dom';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@databro/ui';
import { Chrome, Database, ExternalLink, ShieldCheck } from 'lucide-react';

const WEBSTORE_URL = import.meta.env.VITE_WEBSTORE_URL || '';

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
        {n}
      </span>
      <div className="pt-0.5 text-sm text-muted-foreground">{children}</div>
    </li>
  );
}

export default function Install() {
  const listingReady = WEBSTORE_URL.length > 0;

  return (
    <div className="mx-auto max-w-2xl space-y-8 p-6 md:p-10">
      <div className="space-y-3">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <div className="flex size-7 items-center justify-center rounded-md bg-gradient-to-br from-primary to-primary/80 text-primary-foreground">
            <Database className="size-4" />
          </div>
          <span className="font-medium">DataBro</span>
        </Link>
        <h1 className="text-3xl font-semibold tracking-tight">
          Install the DataBro extension
        </h1>
        <p className="text-muted-foreground">
          One-click install from the Chrome Web Store. Auto-updates run in the
          background — you'll never need to reinstall.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Chrome className="size-5 text-primary" />
            Install from Chrome Web Store
          </CardTitle>
          <CardDescription>
            Works on macOS, Windows, Linux, and ChromeOS. Survives Chrome
            restarts and updates itself.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {listingReady ? (
            <Button asChild size="lg" className="w-full sm:w-auto">
              <a href={WEBSTORE_URL} target="_blank" rel="noopener noreferrer">
                <Chrome className="mr-2 size-4" />
                Add to Chrome
                <ExternalLink className="ml-2 size-3.5" />
              </a>
            </Button>
          ) : (
            <div className="rounded-lg border border-dashed bg-muted/40 p-4">
              <p className="text-sm font-medium">
                Listing pending Chrome Web Store review
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                The extension has been submitted to the Web Store and is
                awaiting Google's review (usually a few hours to a few days
                for the first submission). This page will show an{' '}
                <span className="font-medium">Add to Chrome</span> button as
                soon as it's approved.
              </p>
              <p className="mt-2 text-xs text-muted-foreground">
                Ask an admin if you need access urgently — we can add you as a
                trusted tester for an immediate pre-review install.
              </p>
            </div>
          )}

          <ol className="space-y-3 pt-2">
            <Step n={1}>
              Click <strong>Add to Chrome</strong> above. The Web Store will
              ask you to confirm — click <strong>Add extension</strong>.
            </Step>
            <Step n={2}>
              Open{' '}
              <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                chrome://extensions
              </code>{' '}
              and confirm DataBro is listed and enabled.
            </Step>
            <Step n={3}>
              Return to the dashboard — the extension status indicator in the
              sidebar should turn green within a few seconds.
            </Step>
          </ol>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="size-4 text-muted-foreground" />
            Why is DataBro unlisted?
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>
            DataBro is an internal team tool. It's published on the Chrome Web
            Store with <strong>Unlisted</strong> visibility, which means it
            doesn't appear in Web Store search and only people with the direct
            link (this page) can install it.
          </p>
          <p>
            Updates we ship roll out automatically to everyone who has it
            installed, usually within a few hours of being merged.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Troubleshooting</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-muted-foreground">
          <div>
            <p className="mb-1 font-medium text-foreground">
              Sidebar status still says "Disconnected"
            </p>
            <p>
              Refresh the dashboard, or open and close the extension's icon in
              the Chrome toolbar to wake the service worker.
            </p>
          </div>
          <div>
            <p className="mb-1 font-medium text-foreground">
              I had an older unpacked / CRX version installed
            </p>
            <p>
              Remove it from{' '}
              <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                chrome://extensions
              </code>{' '}
              first, then install fresh from the Web Store link above. The two
              versions have different extension IDs and can't coexist for the
              same user account.
            </p>
          </div>
          <div>
            <p className="mb-1 font-medium text-foreground">
              "This extension may have been corrupted"
            </p>
            <p>
              Click <em>Repair</em> in{' '}
              <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                chrome://extensions
              </code>
              . If that doesn't help, uninstall and reinstall from the link
              above.
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="border-t pt-6 text-center text-xs text-muted-foreground">
        <Link to="/privacy" className="hover:text-foreground">
          Privacy policy
        </Link>
      </div>
    </div>
  );
}
